// One quote: what they sent, activity, lines, and Send quote.
import { PRICE_OFTEN_WORDS, type QuoteDetail, quoteGreetName, quoteLinkLead, quoteMessage } from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTestMode } from "../../api/hooks";
import { useArchiveQuote, useDeclineQuote, useQuote, useRequest, useRestoreQuote, useSendQuote, type Via } from "../../api/money";
import { useSession } from "../../app/session";
import { Choice, InputText } from "../../components/fields";
import { followUpText, moneyExact, shortDate } from "../../components/format";
import { Icon } from "../../components/Icon";
import { Menu, Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, Details, IconButton, LoadError, Page, Skeleton } from "../../components/parts";
import { PhoneRecord } from "../../components/PhoneRecord";
import { usePhone } from "../../components/usePhone";
import { errText, LINE_OFTEN, LineMore } from "../../components/work";
import { QuoteSections, QuoteTop } from "../QuoteSections";
import { SentByThem } from "../Requests";
import { localDay, QuoteTag, sourceWords } from "./common";
import { formCanEdit, OFTEN_OF } from "./Form";
import { Walkthroughs } from "./Pipeline";

export function QuotePage({ id }: { id: string }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const quote = useQuote(id);
  const decline = useDeclineQuote();
  const archive = useArchiveQuote();
  const restore = useRestoreQuote();
  const [open, setOpen] = useState<"send" | null>(null);
  const phone = usePhone();
  const q = quote.data;
  if (quote.error)
    return (
      <Page title="Quote">
        <LoadError error={quote.error} retry={() => void quote.refetch()} />
      </Page>
    );
  if (!q)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("quote.update");
  const closed = q.status === "converted" || q.status === "declined";
  const item = (label: string, run: () => void, close: () => void) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        close();
        run();
      }}
    >
      {label}
    </button>
  );
  const primary =
    q.status === "approved" && can("job.update") ? (
      <Button icon="jobs" onClick={() => void navigate({ to: "/jobs/new", search: { quoteId: q.id } })}>
        Make into a job
      </Button>
    ) : q.status === "converted" && q.jobId ? (
      <Button icon="jobs" onClick={() => void navigate({ to: "/jobs/$id", params: { id: q.jobId ?? "" } })}>
        Open the job
      </Button>
    ) : editable && !closed && q.status !== "approved" ? (
      <Button icon="mail" onClick={() => setOpen("send")}>
        {q.status === "draft" ? (q.proposal ? "Send proposal" : "Send quote") : "Send again"}
      </Button>
    ) : null;
  const secondary =
    q.proposal && q.status === "draft" && editable ? (
      <Button type="secondary" onClick={() => void navigate({ to: "/proposals/$id", params: { id: q.id } })}>
        Edit proposal
      </Button>
    ) : editable && formCanEdit(q) ? (
      <Button type="secondary" icon="edit" onClick={() => void navigate({ to: "/quotes/$id/edit", params: { id: q.id } })}>
        Edit
      </Button>
    ) : null;
  const moreMenu = (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(close) => (
        <>
          {item("Preview as the client sees it", () => void navigate({ to: "/preview/quote/$id", params: { id: q.id } }), close)}
          {item(
            "Copy link",
            () => {
              void navigator.clipboard.writeText(q.link);
              toast("Quote link copied");
            },
            close,
          )}
          {editable && !closed
            ? item(
                "Decline work",
                () =>
                  decline.mutate(q.id, {
                    onSuccess: () => toast("Quote marked declined"),
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  }),
                close,
              )
            : null}
          {editable && q.status !== "converted"
            ? item(
                "Archive",
                () =>
                  archive.mutate(q.id, {
                    onSuccess: () => {
                      toast(`Quote #${String(q.number)} archived`, {
                        undo: () =>
                          restore.mutate(q.id, {
                            onSuccess: () => void navigate({ to: "/quotes/$id", params: { id: q.id } }),
                          }),
                      });
                      void navigate({ to: "/quotes", search: {} });
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  }),
                close,
              )
            : null}
        </>
      )}
    </Menu>
  );
  const signed = q.signature ? (
    <Banner type="success">
      Approved by <b>{q.signature.name}</b> on {shortDate(localDay(q.signature.at))}.{" "}
      {q.signature.drawing ? "They typed their name and signed by hand." : "They typed their name to sign."}
      {q.signature.drawing ? (
        <img className="sign-shown" src={q.signature.drawing} alt={`Signed by ${q.signature.name}`} />
      ) : null}
    </Banner>
  ) : null;
  const mainCards = (
    <>
      <Card title="Product and service" flush>
        <QuoteLines q={q} />
      </Card>
      <QuoteSections q={q} editable={editable} />
      {q.terms ? (
        <Card title="Terms">
          <p className="pre-wrap">{q.terms}</p>
        </Card>
      ) : null}
    </>
  );
  const detailsCard = (
    <Card title="Details">
      <Details
        rows={[
          ["Created", shortDate(localDay(q.createdAt))],
          ["Sent", q.sentAt ? shortDate(localDay(q.sentAt)) : "Not yet"],
          [
            "Opened",
            q.views
              ? `${String(q.views)} ${q.views === 1 ? "time" : "times"}, last ${q.lastViewedAt ? shortDate(localDay(q.lastViewedAt)) : ""}`
              : "Not yet",
          ],
          ["How often", q.frequency === "one-time" ? "One time" : PRICE_OFTEN_WORDS[OFTEN_OF[q.frequency] ?? "once"]],
          ["Card to approve", q.cardRequired ? "Needed" : "Not needed"],
          ["Good until", q.expiresOn ? shortDate(q.expiresOn) : "No end"],
          [
            "Deposit",
            q.deposit
              ? `${moneyExact(q.depositCents)}${q.deposit.kind === "percent" ? ` (${String(q.deposit.value)}%)` : ""}`
              : "None",
          ],
        ]}
      />
      {q.depositInvoiceId ? (
        <Button
          type="tertiary"
          size="small"
          icon="invoices"
          onClick={() => void navigate({ to: "/invoices/$id", params: { id: q.depositInvoiceId ?? "" } })}
        >
          Deposit invoice
        </Button>
      ) : null}
    </Card>
  );
  const sheets = <>{open === "send" ? <SendQuote q={q} open onClose={() => setOpen(null)} /> : null}</>;

  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/quotes", search: {} }) }}
        status={
          <>
            <QuoteTag status={q.status} />
            <span className="muted num">Quote #{String(q.number)}</span>
          </>
        }
        title={moneyExact(q.totalCents)}
        sub={
          q.clientId ? (
            <button
              type="button"
              className="link"
              onClick={() => void navigate({ to: "/clients/$id", params: { id: q.clientId ?? "" } })}
            >
              {q.name} <Icon name="chevronRight" size={16} />
            </button>
          ) : (
            q.name
          )
        }
        phone={q.contactPhone}
        email={q.contactEmail}
        facts={[
          ...(q.title ? [{ key: "what", icon: "quotes" as const, main: q.title }] : []),
          ...(q.address
            ? [{ key: "where", icon: "pin" as const, main: q.address, sub: "Directions", directions: q.address }]
            : []),
          {
            key: "sent",
            icon: "mail" as const,
            main: q.sentAt ? `Sent ${shortDate(localDay(q.sentAt))}` : "Not sent yet",
            sub: q.views ? `Opened ${String(q.views)} ${q.views === 1 ? "time" : "times"}` : q.sentAt ? "Not opened yet" : "",
          },
        ]}
        actions={
          <>
            {primary}
            {secondary}
            {moreMenu}
          </>
        }
      >
        {signed}
        {mainCards}
        {q.leadId ? <WhatTheySent leadId={q.leadId} /> : null}
        {detailsCard}
        <Walkthroughs quoteId={q.id} />
        <QuoteActivity q={q} />
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={q.title ?? `Quote for ${q.name}`}
      titleMetaData={<QuoteTag status={q.status} />}
      primaryAction={primary}
      secondaryAction={secondary}
      moreActions={moreMenu}
    >
      <QuoteTop q={q} editable={editable} />
      {signed}
      <div className="home-grid">
        <div className="stack">{mainCards}</div>
        <div className="stack">
          {q.depositInvoiceId ? (
            <Button
              type="secondary"
              icon="invoices"
              onClick={() => void navigate({ to: "/invoices/$id", params: { id: q.depositInvoiceId ?? "" } })}
            >
              Deposit invoice
            </Button>
          ) : null}
          {q.leadId ? <WhatTheySent leadId={q.leadId} /> : null}
          <Walkthroughs quoteId={q.id} />
          <QuoteActivity q={q} />
        </div>
      </div>
      {sheets}
    </Page>
  );
}

/** The old Steward's "What they sent from the website": the request this quote came from. */
function WhatTheySent({ leadId }: { leadId: string }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const req = useRequest(leadId, can("lead.read"));
  const r = req.data;
  if (!r) return null;
  return (
    <Card
      title="What they sent"
      action={
        <Button type="tertiary" size="small" onClick={() => void navigate({ to: "/requests/$id", params: { id: r.id } })}>
          Open request
        </Button>
      }
    >
      <div className="stack">
        <SentByThem r={r} />
        <Details
          rows={[
            ["Service", r.service],
            ["Found us", sourceWords(r.source)],
          ]}
        />
      </div>
    </Card>
  );
}

/** What happened to the quote, newest first, from its own dates. */
function QuoteActivity({ q }: { q: QuoteDetail }) {
  const when = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  const events: [string, string | null][] = [
    ["Created", q.createdAt],
    ["Sent", q.sentAt],
    [q.views ? `Opened by the client ${q.views === 1 ? "once" : `${String(q.views)} times`}` : "", q.lastViewedAt],
    [q.signature ? `Approved by ${q.signature.name}` : q.approvedAt ? "Approved" : "", q.signature?.at ?? q.approvedAt],
    ...q.followUps.map((f): [string, string] => [followUpText(f), f.at]),
  ];
  const shown = events
    .filter((e): e is [string, string] => Boolean(e[0]) && Boolean(e[1]))
    .sort((a, b) => b[1].localeCompare(a[1]));
  return (
    <Card title="Activity">
      <ul className="activity-list">
        {q.status === "converted" ? (
          <li>
            <span>Made into a job</span>
          </li>
        ) : q.status === "declined" ? (
          <li>
            <span>Declined</span>
          </li>
        ) : null}
        {shown.map(([what, at], n) => (
          <li key={n}>
            <span>{what}</span>
            <span className="muted small num">{when(at)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function QuoteLines({ q }: { q: QuoteDetail }) {
  const chosen = q.lines.filter((l) => !l.optional || l.picked);
  const subtotal = chosen.reduce((s, l) => s + l.amountCents, 0);
  // A first visit with one-time work on top (a first cut of tall grass) costs more than each repeat visit after it.
  const each = chosen.filter((l) => l.recurring).reduce((s, l) => s + l.amountCents, 0);
  const repeat = q.frequency !== "one-time" && each > 0 && each < subtotal ? each : null;
  return (
    <table className="data lines">
      <thead className="lines-head">
        <tr>
          <th>Line item</th>
          <th className="right">Quantity</th>
          <th className="right">Unit price</th>
          <th className="right">Total</th>
        </tr>
      </thead>
      <tbody>
        {q.lines.map((l) => (
          <tr key={l.id} className={l.optional && !l.picked ? "inactive" : undefined}>
            <td>
              <b>{l.description}</b>
              {l.optional ? <span className="muted"> · Add-on{l.picked ? ", picked" : ""}</span> : null}
              {q.frequency !== "one-time" && !l.recurring ? <span className="muted"> · First visit only</span> : null}
              <LineMore line={l} basis={false} />
            </td>
            <td className="right num">{l.quantity}</td>
            <td className="right num muted">{moneyExact(l.unitPriceCents)}</td>
            <td className="right num">{moneyExact(l.amountCents)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3}>Subtotal</td>
          <td className="right num">{moneyExact(subtotal)}</td>
        </tr>
        {q.deposit ? (
          <tr>
            <td colSpan={3}>Deposit asked on approval</td>
            <td className="right num">{moneyExact(q.depositCents)}</td>
          </tr>
        ) : null}
        <tr>
          <td colSpan={3}>
            <b>{repeat ? "First visit total" : "Total"}</b>
          </td>
          <td className="right num">
            <b>{moneyExact(q.totalCents)}</b>
          </td>
        </tr>
        {repeat ? (
          <tr>
            <td colSpan={3}>Then {(LINE_OFTEN[q.frequency] ?? "each visit").toLowerCase()}</td>
            <td className="right num">{moneyExact(repeat)}</td>
          </tr>
        ) : null}
      </tfoot>
    </table>
  );
}

function SendQuote({ q, open, onClose }: { q: QuoteDetail; open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const testMode = useTestMode(company.id);
  const send = useSendQuote();
  const toast = useToast();
  const [via, setVia] = useState<Via>(q.contactPhone ? "text" : "email");
  const [typed, setTyped] = useState<string | null>(null);
  const to = typed ?? (via === "text" ? q.contactPhone : q.contactEmail) ?? "";
  const first = quoteGreetName(q.contactName ?? q.name, q.title);
  // The words are theirs to change; the link lines stay as they are (the server adds them back).
  const lead = quoteLinkLead(q.proposal);
  const full = quoteMessage({ brand: q.brand || company.name, first, title: q.title, proposal: q.proposal, via }, q.link);
  const linkLine = `${lead} ${q.link}`;
  const button = q.proposal ? "See your proposal" : "View quote";
  const stock = full
    .split("\n")
    .filter((l) => !l.includes(q.link) && l.trim() !== lead)
    .join("\n")
    .trim();
  // Untouched words follow Text or Email (a proposal's text is shorter); typed words stay as typed.
  const [edited, setWords] = useState<string | null>(null);
  const words = edited ?? stock;
  const changed = words.trim() !== stock;
  return (
    <Modal
      title={q.proposal ? "Send proposal" : "Send quote"}
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="mail"
            disabled={to.trim().length < 3 || !words.trim()}
            loading={send.isPending}
            onClick={() =>
              send.mutate(
                { id: q.id, via, to: to.trim(), ...(changed ? { message: words.trim() } : {}) },
                {
                  onSuccess: () => {
                    toast(
                      testMode
                        ? `${q.proposal ? "Proposal" : "Quote"} saved for ${to.trim()}. Test mode, so it waits in the outbox.`
                        : `${q.proposal ? "Proposal" : "Quote"} sent to ${to.trim()}.`,
                    );
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Send {via === "text" ? "text" : "email"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<Via>
          label="Send by"
          value={via}
          onChange={(v) => {
            setVia(v);
            setTyped(null);
          }}
          options={[
            { value: "text", label: "Text" },
            { value: "email", label: "Email" },
          ]}
        />
        <InputText label="Send to" value={to} onChange={(e) => setTyped(e.target.value)} />
        <label className="field wide message-box">
          <span className="label">The message</span>
          <textarea rows={q.proposal ? 8 : 4} maxLength={1400} value={words} onChange={(e) => setWords(e.target.value)} />
          <span className="message-locked" aria-label="Quote link, can't be changed">
            {via === "email" ? `${button} button` : linkLine}
          </span>
          {via === "email" ? <span className="description">The email ends with a {button} button to open it.</span> : null}
        </label>
        {testMode ? (
          <p className="muted small">Texts and emails are in test mode. This goes to the outbox and is not sent.</p>
        ) : null}
      </div>
    </Modal>
  );
}

// Pipeline -------------------------------------------------------------------------------------------------------------
